const enabledNamespaces = process.env.NEXT_PUBLIC_DEBUG_LOG?.split(',') ?? []
const allEnabled = enabledNamespaces.includes('*')

const DEEP_DIR_OPTS = { depth: null as number | null, colors: true }

/**
 * Print one arg. Object/array args go through `console.dir` with
 * `depth: null` so nested carrier blobs / shipment payloads aren't
 * truncated to `[Object]` / `[Array]` (Node's default). Primitives stay
 * on the original `console.log/info/warn/error` channel so the level's
 * visual styling is preserved.
 */
const printDeep = (consoleFn: (...args: unknown[]) => void, prefix: string, args: unknown[]) => {
  const primitives: unknown[] = []
  const objects: unknown[] = []
  args.forEach((a) => {
    if (typeof a === 'object' && a !== null) objects.push(a)
    else primitives.push(a)
  })
  if (prefix || primitives.length) consoleFn(prefix, ...primitives)
  objects.forEach((o) => {
    console.dir(o, DEEP_DIR_OPTS)
  })
}

export function createLogger(...namespaces: string[]) {
  const isEnabled = allEnabled || enabledNamespaces.some((item) => namespaces.includes(item))
  const prefix = namespaces.map((n) => `[${n}]`).join(' ')

  return {
    /** Whether this namespace is switched on via NEXT_PUBLIC_DEBUG_LOG. Lets
     *  callers skip work that only exists to produce log arguments. */
    isEnabled,
    log: (...args: unknown[]) => {
      if (isEnabled) printDeep(console.log, prefix, args)
    },
    error: (...args: unknown[]) => {
      if (isEnabled) printDeep(console.error, prefix, args)
    },
    warn: (...args: unknown[]) => {
      if (isEnabled) printDeep(console.warn, prefix, args)
    },
    info: (...args: unknown[]) => {
      if (isEnabled) printDeep(console.info, prefix, args)
    }
  }
}

export type Logger = ReturnType<typeof createLogger>
